import './FaqOverview.scss'

import queryString from 'query-string'
import React, {useEffect, useRef, useState} from 'react'
import AnimateHeight from 'react-animate-height'
import {Link} from 'react-router-dom'
import {Answer, JSONLD, Question} from 'react-structured-data'
import {WysiwygType} from '../../../types'
import {style} from '../../../util/style'
import {Theme} from '../../Theme'
import {Icon} from '../../ui/Icon'

const styles = style('faqoverview', ['category', 'item', 'question', 'answer'])

export type FaqOverviewType = {
	categories: Array<FaqOverviewCategoryType>
}

type FaqOverviewCategoryType = {
	title: string
	path: string
	children: Array<FaqOverviewItemType>
}

type FaqOverviewItemType = {
	catPath: string
	title: string
	path: string
	question: string
	answer: WysiwygType
}

export const FaqOverview: React.FC<FaqOverviewType> = ({categories}) => {
	if (!categories || categories.length === 0) return null

	return (
		<div className={styles()}>
			<Theme.Container>
				{categories.map((item) => (
					<FaqOverviewCategory key={item.path} {...item} />
				))}
			</Theme.Container>
		</div>
	)
}

const FaqOverviewCategory: React.FC<FaqOverviewCategoryType> = ({
	title,
	path,
	children
}) => {
	const ref = useRef(null)
	if (!children) return null

	useEffect(() => {
		const {cat, q} = queryString.parse(location.search)
		if (cat !== path) return
		if (!q) ref.current.scrollIntoView({behavior: 'smooth'})
	}, [])

	return (
		<div ref={ref} id={path} className={styles.category()}>
			{title && (
				<Theme.H2 className={styles.category('title')}>{title}</Theme.H2>
			)}
			{children.map((item) => (
				<FaqOverviewItem key={item.path} catPath={path} {...item} />
			))}
		</div>
	)
}

const FaqOverviewItem: React.FC<FaqOverviewItemType> = ({
	catPath,
	title,
	path,
	question,
	answer
}) => {
	const ref = useRef(null)
	const [isOpen, setOpen] = useState(false)
	if (!title || !answer) return null

	useEffect(() => {
		const {cat, q} = queryString.parse(location.search)
		if (cat !== catPath || !q || q !== path) return
		setOpen(true)
		ref.current.scrollIntoView({behavior: 'smooth'})
	}, [])

	return (
		<dl ref={ref} className={styles.item()}>
			<FaqOverviewQuestion
				catPath={catPath}
				path={path}
				question={question || title}
				open={isOpen}
				toggle={() => setOpen(!isOpen)}
			/>
			<AnimateHeight height={isOpen ? 'auto' : 0}>
				<FaqOverviewAnswer questionPath={path} answer={answer} />
			</AnimateHeight>
			<JSONLD>
				<Question name={question || title}>
					<Answer text={answer.replace(/<\/?[^>]+(>|$)/g, '')} />
				</Question>
			</JSONLD>
		</dl>
	)
}

const FaqOverviewQuestion: React.FC<{
	catPath: string
	path: string
	question: string
	open: boolean
	toggle: () => void
}> = ({catPath, path, question, open, toggle}) => (
	<dt id={path} className={styles.question().is({open})}>
		<Link
			to={!open ? `?cat=${catPath}&q=${path}` : '?'}
			replace
			onClick={toggle}
			aria-controls={'aria-' + path}
			aria-expanded={open}
			className={styles.question('link')}
		>
			<Theme.H3 className={styles.question('link-title')}>{question}</Theme.H3>
			<span className={styles.question('link-line')} />
			<span className={styles.question('link-icon')}>
				<Icon icon="chevronDown" />
			</span>
		</Link>
	</dt>
)

const FaqOverviewAnswer: React.FC<{
	questionPath: string
	answer: string
}> = ({questionPath, answer}) => (
	<dd id={'aria-' + questionPath} className={styles.answer()}>
		<Theme.Wysiwyg>{answer}</Theme.Wysiwyg>
	</dd>
)
